import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cn } from '@/lib/cn'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'warn' | 'chat'
type Size = 'sm' | 'md'

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant
  size?: Size
}

// Variants map to the reference `.action-btn` / `.btn-*` / `.doc-action` idioms:
//   primary   → solid brand blue        (.btn-primary)
//   secondary → outlined neutral         (.back-btn, .doc-action)
//   ghost     → transparent hover-fill   (sidebar signout etc)
//   danger    → solid red
//   warn      → amber-tinted            (.btn-warn)
//   chat      → teal-tinted             (.btn-chat)
// SAFT-AI button language (2026-08-26 restyle). Two DS-specific traits worth naming:
//   • primary HOVERS LIGHTER (--ds-primary-pressed) with the CTA glow — after the token
//     rebase, the old hover:bg-brand-900 would have gone near-black navy;
//   • secondary is the DS's FILLED light-blue, not an outlined neutral. If a dense screen
//     turns noisy, demote its call sites to `ghost` — don't soften the variant.
// warn/chat keep staff-only semantics (no DS counterpart).
const variantStyles: Record<Variant, string> = {
  primary:
    'bg-brand-800 text-white border border-brand-800 hover:bg-brand-500 hover:border-brand-500 hover:shadow-cta disabled:bg-brand-800/40 disabled:border-brand-800/40 disabled:shadow-none',
  secondary:
    'bg-brand-100 text-gray-900 border border-brand-100 hover:bg-brand-500 hover:border-brand-500 hover:text-white disabled:opacity-50',
  ghost: 'bg-transparent text-gray-700 hover:bg-surface-muted',
  danger: 'bg-red-500 text-white border border-red-500 hover:bg-red-700 disabled:bg-red-500/40',
  warn: 'bg-amber-100 text-amber-700 border border-amber-500/60 hover:bg-amber-100/70',
  chat: 'bg-teal-100 text-teal-700 border border-teal-500/60 hover:bg-teal-100/70',
}

const sizeStyles: Record<Size, string> = {
  sm: 'h-7 px-3 text-[11px]',
  md: 'h-8 px-3.5 text-[12px]',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', className, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex items-center justify-center gap-1.5 rounded-sm font-semibold transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-800/25 focus-visible:ring-offset-1 focus-visible:ring-offset-surface',
        'disabled:cursor-not-allowed',
        variantStyles[variant],
        sizeStyles[size],
        className,
      )}
      {...rest}
    />
  )
})
